import { type ReactNode, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Activity,
  AlertCircle,
  ArrowRightLeft,
  Calendar,
  CloudUpload,
  Clock,
  ExternalLink,
  Eye,
  FileSearch,
  FileText,
  Hand,
  Inbox,
  Mail,
  MessageSquare,
  MousePointer,
  Pencil,
  Phone,
  Pause,
  Play,
  Plus,
  Reply,
  RotateCcw,
  Send,
  Square,
  Star,
  StickyNote,
  Target,
  Trophy,
  UserCog,
  UserMinus,
  UserPlus,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import {
  formatDate,
  formatDateTime,
  formatRelativeDate,
  initials,
} from "@/lib/formatters";
import { getStatusColor } from "@/lib/constants";
import { formatPhone, isPhoneNumber, phoneForCopy } from "@/lib/phone";
import { CopyButton } from "@/components/shared/CopyButton";
import { rowAnim, rowTransition } from "@/components/ui/motion";
import { useAuthStore } from "@/stores/auth.store";
import { EmailDetailSheet, type EmailStatus } from "./EmailDetailSheet";
import type {
  ActivityLog,
  EmbeddedActivity,
  EmbeddedContact,
  PipelineStatus,
  ProjectMember,
  User,
} from "@/api/types";
import { HiddenDetail, isHidden } from "@/components/shared/HiddenDetail";

type AnyActivity = EmbeddedActivity | ActivityLog;

interface ActivityTimelineProps {
  items: AnyActivity[];
  members?: ProjectMember[];
  pipelineStatuses?: PipelineStatus[];
  contacts?: EmbeddedContact[];
  /** Threaded into the email_replied row so the "View in Lemlist" link can target this lead. */
  lemlistLeadUrl?: string | null;
}

interface ActionConfig {
  icon: LucideIcon;
  dot: string;
  label: string;
}

const ACTIONS: Record<string, ActionConfig> = {
  company_created: { icon: Plus, dot: "bg-slate-300", label: "Company added" },
  company_imported: {
    icon: CloudUpload,
    dot: "bg-slate-400",
    label: "Imported from CSV",
  },
  company_updated: {
    icon: Pencil,
    dot: "bg-slate-400",
    label: "Company updated",
  },
  metadata_updated: {
    icon: Pencil,
    dot: "bg-slate-400",
    label: "Profile updated",
  },
  scored: { icon: Target, dot: "bg-indigo-400", label: "Scored" },
  status_changed: {
    icon: ArrowRightLeft,
    dot: "bg-blue-400",
    label: "Status changed",
  },
  owner_changed: { icon: UserCog, dot: "bg-cyan-400", label: "Owner changed" },
  score_overridden: {
    icon: Hand,
    dot: "bg-amber-500",
    label: "Score overridden",
  },
  score_override_cleared: {
    icon: RotateCcw,
    dot: "bg-amber-400",
    label: "Score override removed",
  },
  email_sent: { icon: Send, dot: "bg-blue-500", label: "Email sent" },
  email_manual_sent: {
    icon: Reply,
    dot: "bg-blue-500",
    label: "You replied",
  },
  email_opened: { icon: Eye, dot: "bg-emerald-500", label: "Email opened" },
  email_clicked: {
    icon: MousePointer,
    dot: "bg-violet-500",
    label: "Link clicked in email",
  },
  email_replied: {
    icon: MessageSquare,
    dot: "bg-emerald-600",
    label: "Lead replied!",
  },
  email_bounced: {
    icon: AlertCircle,
    dot: "bg-rose-500",
    label: "Email bounced",
  },
  email_unsubscribed: {
    icon: UserMinus,
    dot: "bg-slate-400",
    label: "Lead unsubscribed",
  },
  email_sequence_started: {
    icon: Play,
    dot: "bg-indigo-500",
    label: "Email sequence started",
  },
  email_sequence_stopped: {
    icon: Square,
    dot: "bg-slate-400",
    label: "Email sequence stopped",
  },
  // The Email module (campaigns). Membership changes, by a person.
  email_campaign_added: {
    icon: UserPlus,
    dot: "bg-indigo-400",
    label: "Added to an email campaign",
  },
  email_campaign_paused: {
    icon: Pause,
    dot: "bg-amber-400",
    label: "Email campaign paused",
  },
  email_campaign_resumed: {
    icon: Play,
    dot: "bg-indigo-400",
    label: "Email campaign resumed",
  },
  email_campaign_removed: {
    icon: UserMinus,
    dot: "bg-slate-400",
    label: "Removed from an email campaign",
  },
  email_campaign_moved: {
    icon: Mail,
    dot: "bg-indigo-400",
    label: "Moved to another email campaign",
  },
  call_logged: { icon: Phone, dot: "bg-purple-500", label: "Call logged" },
  // A salesperson with a call limit was handed this company's numbers.
  call_lead_granted: {
    icon: Phone,
    dot: "bg-purple-300",
    label: "Number handed out",
  },
  call_scheduled: {
    icon: Phone,
    dot: "bg-purple-400",
    label: "Call scheduled",
  },
  meeting_booked: {
    icon: Calendar,
    dot: "bg-purple-500",
    label: "Meeting booked",
  },
  note_added: { icon: StickyNote, dot: "bg-amber-400", label: "Note added" },
  deal_won: { icon: Trophy, dot: "bg-emerald-500", label: "Deal won" },
  deal_lost: { icon: XCircle, dot: "bg-rose-500", label: "Deal lost" },
  cfo_research_completed: {
    icon: FileSearch,
    dot: "bg-indigo-400",
    label: "CFO research",
  },
};

const FALLBACK: ActionConfig = {
  icon: Activity,
  dot: "bg-slate-300",
  label: "",
};

const CHANGE_ACTIONS = new Set([
  "status_changed",
  "owner_changed",
  "company_updated",
  "metadata_updated",
  "score_overridden",
  "score_override_cleared",
  "company_imported",
]);

/** Email actions whose body, when present in `details.body`, surfaces in
 *  the EmailDetailSheet. Listed separately from CLICKABLE_ACTIONS because
 *  clickability depends on the per-row body — events without a body
 *  (e.g. an opened/clicked notification) shouldn't be clickable. */
const EMAIL_BODY_ACTIONS = new Set([
  "email_sent",
  "email_replied",
  "email_manual_sent",
  "email_sequence_started",
]);

function activityHasBody(item: AnyActivity): boolean {
  if (!EMAIL_BODY_ACTIONS.has(item.action)) return false;
  const d = (item.details ?? {}) as Record<string, unknown>;
  return typeof d.body === "string" && d.body.length > 0;
}

function isItemClickable(item: AnyActivity): boolean {
  if (item.action === "call_logged") return true;
  return activityHasBody(item);
}

type Filter = "all" | "calls" | "emails" | "changes";

const FILTERS: Array<{
  key: Filter;
  label: string;
  match: (a: AnyActivity) => boolean;
}> = [
  { key: "all", label: "All", match: () => true },
  {
    key: "calls",
    label: "Calls",
    match: (a) => a.action === "call_logged",
  },
  {
    key: "emails",
    label: "Emails",
    match: (a) => a.action.startsWith("email_"),
  },
  {
    key: "changes",
    label: "Changes",
    match: (a) => CHANGE_ACTIONS.has(a.action),
  },
];

/** Show the most recent 5 activities by default — most cards
 *  have a long tail of automated events (status changes, score
 *  updates) that drown out the recent human-driven rows. */
const INITIAL_COUNT = 5;
/** Each "Show more" click reveals 10 more rows. */
const LOAD_MORE_COUNT = 10;

export function ActivityTimeline({
  items,
  members = [],
  pipelineStatuses = [],
  contacts = [],
  lemlistLeadUrl = null,
}: ActivityTimelineProps) {
  const currentUser = useAuthStore((s) => s.user);
  const [filter, setFilter] = useState<Filter>("all");
  const [openItem, setOpenItem] = useState<AnyActivity | null>(null);
  const [displayCount, setDisplayCount] = useState(INITIAL_COUNT);

  // Switching filter resets the visible slice — the operator's
  // "Show more" history is per-filter, not global.
  useEffect(() => {
    setDisplayCount(INITIAL_COUNT);
  }, [filter]);

  const counts = useMemo(() => {
    const out: Record<Filter, number> = {
      all: items.length,
      calls: 0,
      emails: 0,
      changes: 0,
    };
    for (const f of FILTERS) {
      if (f.key === "all") continue;
      out[f.key] = items.filter(f.match).length;
    }
    return out;
  }, [items]);

  const filtered = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter);
    if (!f) return items;
    return items.filter(f.match);
  }, [items, filter]);

  // Progressive disclosure: render the first `displayCount` rows;
  // the rest sit behind the "Show more" button until the operator
  // asks for them.
  const visible = useMemo(
    () => filtered.slice(0, displayCount),
    [filtered, displayCount],
  );
  const hasMore = filtered.length > displayCount;
  const remaining = filtered.length - displayCount;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {FILTERS.map((f) => {
          const active = filter === f.key;
          const count = counts[f.key];
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={cn(
                "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                active
                  ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
              )}
            >
              {f.label}
              {f.key !== "all" && (
                <span
                  className={cn(
                    "ml-1.5 tabular-nums",
                    active ? "text-indigo-500" : "text-slate-400",
                  )}
                >
                  ({count})
                </span>
              )}
            </button>
          );
        })}
      </div>

      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-400">
          No activity yet
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-400">
          <Inbox className="mx-auto mb-2 h-5 w-5 text-slate-300" />
          No matching activity
        </div>
      ) : (
        <ol className="relative space-y-4 pl-6">
          <span
            aria-hidden
            className="absolute left-[7px] bottom-2 top-2 w-px bg-slate-200"
          />
          <AnimatePresence initial={false} mode="popLayout">
            {visible.map((item) => {
              const config = ACTIONS[item.action] ?? {
                ...FALLBACK,
                label: humanize(item.action),
              };
              const Icon = config.icon;
              const author = resolveAuthor(item, members, currentUser);
              const clickable = isItemClickable(item);
              const hasBody = activityHasBody(item);
              const dynamicLabel = resolveDynamicLabel(item, config.label);
              const inner = (
                <>
                  <span
                    className={cn(
                      "absolute -left-6 top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full ring-2 ring-white",
                      config.dot,
                    )}
                  >
                    <Icon className="h-2 w-2 text-white" aria-hidden />
                  </span>
                  <div className="space-y-0.5">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span
                        className={cn(
                          "text-sm font-medium text-slate-900",
                          item.action === "email_replied" &&
                            "text-emerald-700",
                        )}
                      >
                        {dynamicLabel || humanize(item.action)}
                        {item.action === "email_replied" && (
                          <Star
                            className="ml-1 inline h-3 w-3 fill-emerald-500 text-emerald-500 align-[-1px]"
                            aria-hidden
                          />
                        )}
                        {hasBody && (
                          <FileText
                            className="ml-1 inline h-3 w-3 text-slate-400 align-[-1px]"
                            aria-label="Email content available"
                          />
                        )}
                      </span>
                      <span className="text-xs text-slate-400">
                        {author && <>by {author} · </>}
                        {formatRelativeDate(item.created_at)}
                      </span>
                    </div>
                    {renderCompactDetails(
                      item,
                      pipelineStatuses,
                      members,
                      lemlistLeadUrl,
                    )}
                  </div>
                </>
              );
              return (
                <motion.li
                  key={item.id}
                  layout
                  initial={rowAnim.initial}
                  animate={rowAnim.animate}
                  exit={rowAnim.exit}
                  transition={rowTransition}
                  className="relative"
                >
                  {clickable ? (
                    <button
                      type="button"
                      onClick={() => setOpenItem(item)}
                      className="-mx-2 flex w-[calc(100%+1rem)] items-start gap-0 rounded-lg px-2 py-1 text-left transition-colors duration-150 hover:bg-indigo-500/[0.04]"
                    >
                      {inner}
                    </button>
                  ) : (
                    inner
                  )}
                </motion.li>
              );
            })}
          </AnimatePresence>
        </ol>
      )}

      {hasMore && (
        <button
          type="button"
          onClick={() =>
            setDisplayCount((prev) => prev + LOAD_MORE_COUNT)
          }
          className="w-full rounded-lg py-2 text-center text-sm text-slate-500 transition-colors hover:bg-indigo-50/50 hover:text-indigo-600"
        >
          Show more ({remaining} remaining)
        </button>
      )}

      <Sheet
        open={openItem?.action === "call_logged"}
        onOpenChange={(o) => !o && setOpenItem(null)}
      >
        <SheetContent className="flex w-full flex-col gap-0 sm:max-w-[420px]">
          {openItem?.action === "call_logged" && (
            <CallDetailSheet
              item={openItem}
              members={members}
              currentUser={currentUser}
              pipelineStatuses={pipelineStatuses}
              contacts={contacts}
              onClose={() => setOpenItem(null)}
            />
          )}
        </SheetContent>
      </Sheet>

      <EmailDetailSheet
        open={
          openItem?.action === "email_sent" ||
          openItem?.action === "email_sequence_started" ||
          openItem?.action === "email_replied" ||
          openItem?.action === "email_manual_sent"
        }
        onClose={() => setOpenItem(null)}
        {...buildEmailSheetPropsFromActivity(openItem, contacts)}
      />
    </div>
  );
}


function renderCompactDetails(
  item: AnyActivity,
  pipelineStatuses: PipelineStatus[],
  members: ProjectMember[],
  lemlistLeadUrl: string | null,
): ReactNode {
  const action = item.action;
  const d = (item.details ?? {}) as Record<string, unknown>;

  switch (action) {
    case "company_updated":
    case "metadata_updated":
      return <ChangesList changes={d.changes as ChangeMap | undefined} />;

    case "call_lead_granted": {
      const used = asNumberOrNull(d.used);
      const limit = asNumberOrNull(d.limit);
      return used != null && limit != null ? (
        <span className="text-xs tabular-nums text-slate-500">
          {used} of {limit} numbers for this salesperson
        </span>
      ) : null;
    }

    case "status_changed":
      return (
        <StatusChange
          from={asString(d.from)}
          to={asString(d.to)}
          statuses={pipelineStatuses}
        />
      );

    case "owner_changed":
      return (
        <OwnerChange
          from={asString(d.from)}
          to={asString(d.to)}
          members={members}
        />
      );

    case "score_overridden":
      return (
        <ScoreOverride
          from={asNumberOrNull(d.from)}
          to={asNumberOrNull(d.to)}
          autoScore={asNumberOrNull(d.auto_score)}
        />
      );

    case "score_override_cleared":
      return (
        <ScoreOverrideCleared
          from={asNumberOrNull(d.from)}
          autoScore={asNumberOrNull(d.auto_score)}
        />
      );

    case "email_sent":
    case "email_manual_sent":
      return typeof d.subject === "string" ? (
        <p className="truncate text-xs italic text-slate-500">{d.subject}</p>
      ) : null;

    case "email_opened":
      return <p className="text-xs text-slate-400">via Lemlist</p>;

    case "email_replied":
      return (
        <div className="mt-0.5 space-y-0.5">
          <p className="text-xs text-slate-400">
            Reply content available in your email inbox and in Lemlist
          </p>
          {lemlistLeadUrl && (
            <a
              href={lemlistLeadUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-500"
            >
              View in Lemlist
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      );

    case "email_unsubscribed":
    case "email_bounced":
      return typeof d.reason === "string" ? (
        <p className="text-xs text-slate-500">{d.reason}</p>
      ) : null;

    case "email_campaign_added":
    case "email_campaign_paused":
    case "email_campaign_resumed":
    case "email_campaign_removed":
      return typeof d.campaign_name === "string" ? (
        <p className="truncate text-xs text-slate-500">
          {typeof d.contact_name === "string" ? `${d.contact_name} · ` : ""}
          {d.campaign_name}
        </p>
      ) : null;

    case "email_campaign_moved":
      return typeof d.campaign_name === "string" ? (
        <p className="truncate text-xs text-slate-500">
          {typeof d.contact_name === "string" ? `${d.contact_name} · ` : ""}
          {d.campaign_name} → {typeof d.to_campaign_name === "string" ? d.to_campaign_name : "another campaign"}
        </p>
      ) : null;

    case "email_sequence_stopped":
      return typeof d.campaign_name === "string" ? (
        <p className="text-xs text-slate-400">
          From campaign: {d.campaign_name}
        </p>
      ) : null;

    case "call_logged":
      return <CallCompact details={d} />;

    case "call_scheduled":
      return typeof d.when === "string" ? (
        <p className="text-xs text-slate-500">{d.when}</p>
      ) : null;

    case "meeting_booked":
      return <MeetingEntry details={d} />;

    case "note_added":
      return typeof d.text === "string" ? (
        <>
          <p className="whitespace-pre-wrap text-xs text-slate-600">{d.text}</p>
          {/* Written in a task's drawer (Prism task #270). */}
          {typeof d.task_title === "string" && d.task_title && (
            <p className="text-xs text-slate-400">From task: {d.task_title}</p>
          )}
        </>
      ) : null;

    case "deal_won":
      return typeof d.value_eur === "number" ? (
        <p className="text-xs text-slate-500">
          {new Intl.NumberFormat("fr-FR", {
            style: "currency",
            currency: "EUR",
            maximumFractionDigits: 0,
          }).format(d.value_eur)}
        </p>
      ) : null;

    case "deal_lost":
      return typeof d.reason === "string" ? (
        <p className="text-xs text-slate-500">{d.reason}</p>
      ) : null;

    case "company_imported":
      return <p className="text-xs text-slate-500">Imported via CSV</p>;

    case "company_created":
      return typeof d.source === "string" ? (
        <p className="text-xs text-slate-500">Source: {d.source}</p>
      ) : null;

    case "scored":
      return typeof d.score === "number" ? (
        <p className="text-xs text-slate-500">Score {d.score}</p>
      ) : null;

    case "cfo_research_completed": {
      const answers =
        (Array.isArray(d.answers) ? d.answers.length : 0) +
        (Array.isArray(d.founders) ? d.founders.length : 0);
      return (
        <div className="space-y-0.5">
          <p className="text-xs text-slate-500">
            {d.result === "not_found"
              ? "Nothing found"
              : `${answers} answer${answers === 1 ? "" : "s"} recorded`}
          </p>
          {typeof d.note === "string" && d.note && (
            <p className="whitespace-pre-wrap text-xs text-slate-600">{d.note}</p>
          )}
        </div>
      );
    }

    default:
      return null;
  }
}

// ─── Compact entry — call ────────────────────────────────────────────────

function CallCompact({ details }: { details: Record<string, unknown> }) {
  const outcome =
    typeof details.outcome === "string" ? humanize(details.outcome) : null;
  const duration =
    typeof details.duration_minutes === "number"
      ? `${details.duration_minutes} min`
      : null;
  const notes = typeof details.notes === "string" ? details.notes : null;

  if (!outcome && !duration && !notes) return null;

  return (
    <div className="mt-0.5 space-y-0.5">
      {(outcome || duration) && (
        <p className="text-xs text-slate-500">
          {[outcome, duration].filter(Boolean).join(" · ")}
        </p>
      )}
      {notes && (
        <p className="truncate text-xs text-slate-600" title={notes}>
          {notes}
        </p>
      )}
    </div>
  );
}

function MeetingEntry({ details }: { details: Record<string, unknown> }) {
  const date =
    typeof details.date === "string" || details.date instanceof Date
      ? formatDate(details.date as string | Date)
      : null;
  const notes = typeof details.notes === "string" ? details.notes : null;

  if (!date && !notes) return null;

  return (
    <div className="mt-0.5 space-y-0.5 text-xs">
      {date && <p className="text-slate-500">Scheduled for {date}</p>}
      {notes && <p className="whitespace-pre-wrap text-slate-600">{notes}</p>}
    </div>
  );
}

// ─── Changes (company_updated, metadata_updated) ─────────────────────────

type ChangeMap = Record<string, { from?: unknown; to?: unknown }>;

function ChangesList({ changes }: { changes: ChangeMap | undefined }) {
  if (!changes || typeof changes !== "object") return null;
  const entries = Object.entries(changes);
  if (entries.length === 0) return null;

  return (
    <ul className="mt-1 space-y-0.5 text-xs">
      {entries.map(([key, change]) => (
        <li key={key} className="flex flex-wrap items-baseline gap-x-1">
          <span className="font-medium text-slate-700">{humanize(key)}:</span>
          <span className="text-slate-400">{formatValue(change?.from)}</span>
          <span className="text-slate-300">→</span>
          <span className="font-medium text-slate-700">
            {formatValue(change?.to)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function StatusChange({
  from,
  to,
  statuses,
}: {
  from: string | null;
  to: string | null;
  statuses: PipelineStatus[];
}) {
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
      {from && <StatusPill statusKey={from} statuses={statuses} />}
      {from && to && <span className="text-slate-300">→</span>}
      {to && <StatusPill statusKey={to} statuses={statuses} />}
    </div>
  );
}

function StatusPill({
  statusKey,
  statuses,
}: {
  statusKey: string;
  statuses: PipelineStatus[];
}) {
  const colors = getStatusColor(statusKey, statuses);
  const label =
    statuses.find((s) => s.key === statusKey)?.label ?? humanize(statusKey);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-1.5 py-0 text-[10px] font-medium",
        colors.bg,
        colors.text,
        colors.border,
      )}
    >
      <span className={cn("h-1 w-1 rounded-full", colors.dot)} />
      {label}
    </span>
  );
}

function OwnerChange({
  from,
  to,
  members,
}: {
  from: string | null;
  to: string | null;
  members: ProjectMember[];
}) {
  const fromName = ownerLabel(from, members);
  const toName = ownerLabel(to, members);
  return (
    <p className="mt-0.5 text-xs text-slate-500">
      <span>{fromName}</span>
      <span className="mx-1 text-slate-300">→</span>
      <span className="font-medium text-slate-700">{toName}</span>
    </p>
  );
}

function ownerLabel(id: string | null, members: ProjectMember[]): string {
  if (!id) return "Unassigned";
  return members.find((m) => m.id === id)?.full_name ?? "Unknown user";
}

function ScoreOverride({
  from,
  to,
  autoScore,
}: {
  from: number | null;
  to: number | null;
  autoScore: number | null;
}) {
  return (
    <p className="mt-0.5 text-xs text-slate-500">
      auto{" "}
      <span className="font-medium text-slate-700">{autoScore ?? "—"}</span>
      <span className="mx-1 text-slate-300">→</span>
      manual{" "}
      <span className="font-medium text-amber-700">{to ?? "—"}</span>
      {typeof from === "number" && from !== to && (
        <span className="text-slate-400"> (was {from})</span>
      )}
    </p>
  );
}

function ScoreOverrideCleared({
  from,
  autoScore,
}: {
  from: number | null;
  autoScore: number | null;
}) {
  return (
    <p className="mt-0.5 text-xs text-slate-500">
      Removed manual override
      {typeof from === "number" && (
        <>
          {" "}(was <span className="font-medium text-slate-700">{from}</span>)
        </>
      )}
      , auto-score is now{" "}
      <span className="font-medium text-slate-700">{autoScore ?? "—"}</span>
    </p>
  );
}

// ─── Call detail sheet ───────────────────────────────────────────────────

const OUTCOME_TONES: Record<
  string,
  { label: string; bg: string; text: string; border: string }
> = {
  interested: {
    label: "Interested",
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-200",
  },
  not_interested: {
    label: "Not interested",
    bg: "bg-rose-50",
    text: "text-rose-700",
    border: "border-rose-200",
  },
  callback: {
    label: "Callback",
    bg: "bg-amber-50",
    text: "text-amber-700",
    border: "border-amber-200",
  },
  voicemail: {
    label: "Voicemail",
    bg: "bg-slate-100",
    text: "text-slate-700",
    border: "border-slate-200",
  },
  no_answer: {
    label: "No answer",
    bg: "bg-slate-100",
    text: "text-slate-700",
    border: "border-slate-200",
  },
  wrong_number: {
    label: "Wrong number",
    bg: "bg-rose-50",
    text: "text-rose-700",
    border: "border-rose-200",
  },
};

interface CallDetailSheetProps {
  item: AnyActivity;
  members: ProjectMember[];
  currentUser: User | null;
  pipelineStatuses: PipelineStatus[];
  contacts: EmbeddedContact[];
  onClose: () => void;
}

function CallDetailSheet({
  item,
  members,
  currentUser,
  pipelineStatuses,
  contacts,
  onClose,
}: CallDetailSheetProps) {
  const d = (item.details ?? {}) as Record<string, unknown>;
  const duration =
    typeof d.duration_minutes === "number" ? d.duration_minutes : null;
  const outcomeKey = typeof d.outcome === "string" ? d.outcome : null;
  const outcome = outcomeKey
    ? (OUTCOME_TONES[outcomeKey] ?? {
        label: humanize(outcomeKey),
        bg: "bg-slate-100",
        text: "text-slate-700",
        border: "border-slate-200",
      })
    : null;
  const notes = typeof d.notes === "string" ? d.notes.trim() : null;
  const contactId = typeof d.contact_id === "number" ? d.contact_id : null;
  const contact = contactId ? contacts.find((c) => c.id === contactId) : null;
  const newStatus = typeof d.new_status === "string" ? d.new_status : null;
  const previousStatus =
    typeof d.previous_status === "string" ? d.previous_status : null;

  const author = resolveAuthor(item, members, currentUser) ?? "—";

  return (
    <>
      <SheetHeader>
        <SheetTitle>Call detail</SheetTitle>
        <SheetDescription className="sr-only">
          Full record of the logged call.
        </SheetDescription>
      </SheetHeader>

      <div className="flex-1 space-y-5 overflow-y-auto py-4">
        <div className="space-y-1">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
            When
          </div>
          <div className="text-sm font-medium text-slate-900">
            {formatDateTime(item.created_at, "PPP 'at' HH:mm")}
          </div>
          <div className="text-xs text-slate-500">
            {formatRelativeDate(item.created_at)}
          </div>
        </div>

        <div className="space-y-1">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
            By
          </div>
          <div className="flex items-center gap-2">
            <Avatar className="h-7 w-7 border border-slate-200">
              <AvatarFallback className="bg-slate-900 text-[10px] font-semibold text-white">
                {initials(author)}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm font-medium text-slate-900">{author}</span>
          </div>
        </div>

        <div className="space-y-3 rounded-xl bg-slate-50 p-5">
          {duration != null && (
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <Clock className="h-3.5 w-3.5 text-slate-400" />
              <span className="font-medium">{duration} minutes</span>
            </div>
          )}
          {outcome && (
            <div
              className={cn(
                "flex items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium",
                outcome.bg,
                outcome.text,
                outcome.border,
              )}
            >
              {outcome.label}
            </div>
          )}
          {newStatus && newStatus !== previousStatus && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>Status</span>
              <span>→</span>
              {previousStatus && (
                <StatusPill
                  statusKey={previousStatus}
                  statuses={pipelineStatuses}
                />
              )}
              {previousStatus && <span>→</span>}
              <StatusPill statusKey={newStatus} statuses={pipelineStatuses} />
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
            Notes
          </div>
          {notes ? (
            <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
              <p className="whitespace-pre-wrap leading-relaxed">{notes}</p>
            </div>
          ) : (
            <p className="text-sm italic text-slate-400">No note added</p>
          )}
        </div>

        {contact && (
          <div className="space-y-2 border-t border-slate-900/[0.06] pt-4">
            <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Contact
            </div>
            <div className="space-y-0.5">
              <div className="text-sm font-medium text-slate-900">
                {contact.full_name}
              </div>
              {contact.title && (
                <div className="text-xs text-slate-500">{contact.title}</div>
              )}
              {isPhoneNumber(contact.phone) && (
                <div className="flex items-center gap-0.5 text-xs text-slate-500">
                  {formatPhone(contact.phone)}
                  <CopyButton value={phoneForCopy(contact.phone)} label="phone number" />
                </div>
              )}
              {isHidden(contact, "phone") && <HiddenDetail label="Phone" className="text-xs" />}
              {contact.email && (
                <div className="flex items-center gap-0.5 text-xs text-indigo-600">
                  {contact.email}
                  <CopyButton value={contact.email} label="email" />
                </div>
              )}
              {isHidden(contact, "email") && <HiddenDetail label="Email" className="text-xs" />}
            </div>
          </div>
        )}
      </div>

      <SheetFooter className="border-t border-slate-900/[0.06] pt-4">
        <Button variant="outline" onClick={onClose} className="w-full">
          Close
        </Button>
      </SheetFooter>
    </>
  );
}

// ─── Email detail sheet — props builder ──────────────────────────────────

/**
 * Maps an email_sent or email_sequence_started activity log into the
 * EmailDetailSheet prop shape. Reads details defensively — different
 * actions populate different keys, and older events may be missing the
 * subject/body entirely. Returns "no-op" props when the item isn't an
 * email so the sheet stays closed.
 */
function buildEmailSheetPropsFromActivity(
  item: AnyActivity | null,
  contacts: EmbeddedContact[],
): {
  subject: string | null;
  body: string | null;
  bodyType: "html" | "text" | null;
  direction: "outbound" | "inbound" | null;
  contactEmail: string | null;
  contactName: string | null;
  sentAt: string | null;
  campaignName: string | null;
  step: number | null;
  status: EmailStatus | null;
  lemlistUrl: string | null;
} {
  const empty = {
    subject: null,
    body: null,
    bodyType: null,
    direction: null,
    contactEmail: null,
    contactName: null,
    sentAt: null,
    campaignName: null,
    step: null,
    status: null,
    lemlistUrl: null,
  } as const;
  if (
    !item ||
    (item.action !== "email_sent" &&
      item.action !== "email_sequence_started" &&
      item.action !== "email_replied" &&
      item.action !== "email_manual_sent")
  ) {
    return empty;
  }
  const d = (item.details ?? {}) as Record<string, unknown>;
  const isReply = item.action === "email_replied";
  const isManual = item.action === "email_manual_sent";
  const contactId = typeof d.contact_id === "number" ? d.contact_id : null;
  const contact = contactId
    ? contacts.find((c) => c.id === contactId) ?? null
    : null;
  const step =
    typeof d.step === "number"
      ? d.step
      : typeof d.sequence_step === "number"
        ? d.sequence_step
        : null;
  const rawStatus = typeof d.status === "string" ? d.status : null;
  const status = isEmailStatus(rawStatus) ? rawStatus : null;
  const body = pickString(
    d,
    "body",
    "email_body",
    "html_body",
    "text_body",
    "content",
    "message",
  );
  const rawBodyType = typeof d.body_type === "string" ? d.body_type : null;
  const bodyType: "html" | "text" | null =
    rawBodyType === "html" || rawBodyType === "text" ? rawBodyType : null;
  // For inbound replies, the "other person" is the prospect (sender_*).
  // For outbound mail, it's the recipient (contact_*).
  const inboundEmail = pickString(d, "sender_email", "from_email", "reply_from");
  const inboundName = pickString(d, "sender_name", "from_name");
  const outboundEmail =
    contact?.email ??
    (typeof d.contact_email === "string" ? d.contact_email : null);
  const outboundName =
    contact?.full_name ??
    (typeof d.contact_name === "string" ? d.contact_name : null);
  return {
    subject: typeof d.subject === "string" ? d.subject : null,
    body,
    bodyType,
    direction: isReply ? "inbound" : "outbound",
    contactEmail: isReply ? inboundEmail ?? outboundEmail : outboundEmail,
    contactName: isReply ? inboundName ?? outboundName : outboundName,
    sentAt: item.created_at,
    campaignName:
      typeof d.campaign_name === "string" ? d.campaign_name : null,
    step,
    // Manual replies aren't part of a sequence step and aren't tied to
    // a Lemlist tracking status — leave the badge off so we don't
    // mislabel them as "Sent" / "Opened" etc.
    status: isReply
      ? "replied"
      : isManual
        ? null
        : item.action === "email_sent"
          ? status ?? "sent"
          : status,
    lemlistUrl:
      typeof d.lemlist_lead_url === "string" ? d.lemlist_lead_url : null,
  };
}

function isEmailStatus(s: string | null): s is EmailStatus {
  return (
    s === "sent" ||
    s === "opened" ||
    s === "clicked" ||
    s === "replied" ||
    s === "bounced"
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function resolveAuthor(
  item: AnyActivity,
  members: ProjectMember[],
  currentUser: User | null,
): string | null {
  if (item.user_id) {
    const member = members.find((m) => m.id === item.user_id);
    if (member) return member.full_name;
    if (currentUser && currentUser.id === item.user_id) {
      return `${currentUser.full_name} (you)`;
    }
    return "Unknown user";
  }
  const source = (item.details as Record<string, unknown> | undefined)?.source;
  if (typeof source === "string" && source.trim() !== "") {
    return `Pipeline · ${source}`;
  }
  return "System";
}

function formatValue(v: unknown): ReactNode {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "string") return humanize(v);
  if (typeof v === "number") return v.toLocaleString("en-US");
  return String(v);
}

function asString(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}

function asNumberOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function pickString(
  obj: Record<string, unknown>,
  ...keys: string[]
): string | null {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "string" && v.length > 0) return v;
  }
  return null;
}

function humanize(s: string): string {
  if (!s) return "";
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Some labels carry context from the action's details (the step number for
 * email_sent, the campaign name for email_sequence_started). Computed at
 * render time rather than hardcoded into ACTIONS, since they vary per row.
 */
function resolveDynamicLabel(item: AnyActivity, baseLabel: string): string {
  const d = (item.details ?? {}) as Record<string, unknown>;
  switch (item.action) {
    case "email_sent": {
      const step =
        typeof d.step === "number"
          ? d.step
          : typeof d.sequence_step === "number"
            ? d.sequence_step
            : null;
      return step != null ? `Email sent · Step ${step}` : baseLabel;
    }
    case "email_sequence_started": {
      const name =
        typeof d.campaign_name === "string" ? d.campaign_name : null;
      return name ? `Email sequence started · ${name}` : baseLabel;
    }
    case "email_manual_sent": {
      // Manual replies sent by an operator. When the backend captured a
      // sender name, surface "{name} replied"; otherwise fall back to
      // "You replied" so the row reads naturally either way.
      const name =
        typeof d.sender_name === "string" && d.sender_name.length > 0
          ? d.sender_name
          : null;
      return name ? `${name} replied` : "You replied";
    }
    default:
      return baseLabel;
  }
}
